import {
  Injectable,
  inject,
  signal,
  PLATFORM_ID,
  REQUEST,
  computed,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import {
  DEFAULT_COUNTRY_CODE,
  resolveCountryFromDomain,
  route,
  seoRoute,
  type CountryCode,
  type LanguageCode,
  type ParamsOf,
  type RouteName,
  type RouteOptions,
  type SeoParamsOf,
  type SeoRouteName,
  resolveBcp47FromLanguageCode,
  getCountryConfig,
  resolveCompensationFromCurrencyCode,
  resolveTrustpilotUrl,
  type CurrencyCode,
  type Statistics,
} from '@handyhand/i18n';
import { LanguageToken } from '@app/core/env.tokens';

export interface TrustpilotProfile {
  businessUnitId: string;
  /** Public review page, e.g. https://dk.trustpilot.com/review/handyhand.dk */
  reviewUrl: string;
  /** Reviews shown are the market's own language, whatever the UI language. */
  reviewLanguage: LanguageCode;
}

/**
 * Resolves country/language from the request hostname and exposes helpers
 * for building localized URLs. URLs always follow the hostname, never a
 * user's profile country.
 *
 * Resolution order for `routingCountryCode`:
 *   1. `resolveCountryFromDomain(hostname)` — production truth.
 *   2. `DEFAULT_COUNTRY_CODE` — final safety net for unrecognized hosts.
 *
 * Works on both platforms:
 *   - Browser: reads `window.location.hostname`.
 *   - SSR: injects Angular 21's `REQUEST` token (Fetch `Request` wired by
 *     `AngularNodeAppEngine.handle(req)`) and reads its URL hostname.
 */
@Injectable({ providedIn: 'root' })
export class LocalizationService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly request = inject(REQUEST, { optional: true });

  private readonly hostname: string = this.resolveHostname();

  /**
   * Country for routes. Resolved ONCE at boot; never mutated.
   * Used by `route()` / `seoRoute()` below and by the route pipes.
   * Independent of `countryCode` below — URLs must follow the hostname,
   * never the user's profile preference.
   */
  public readonly routingCountryCode: CountryCode =
    resolveCountryFromDomain(this.hostname) ?? DEFAULT_COUNTRY_CODE;

  public readonly bcp47 = computed<string>(() =>
    resolveBcp47FromLanguageCode(this.languageCode()),
  );

  public readonly countryCode = signal<CountryCode>(this.routingCountryCode);

  /** Currency for the resolved country ('dkk' on handyhand.dk, 'eur' on .nl). */
  public readonly currencyCode = computed<CurrencyCode>(
    () => getCountryConfig(this.countryCode()).currencyCode,
  );

  /**
   * The market's Trustpilot profile for social proof (stars, review counts),
   * from `COUNTRIES[].trustpilot` in `@handyhand/i18n`. Null where the market
   * hides social proof (NL runs invitations only).
   */
  public readonly trustpilotProfile = computed<TrustpilotProfile | null>(() => {
    const code = this.countryCode();
    const { trustpilot, defaultLanguage } = getCountryConfig(code);
    const reviewUrl = resolveTrustpilotUrl(code, 'review');

    if (!trustpilot.enabled || !trustpilot.socialProofVisible || !reviewUrl) {
      return null;
    }

    return {
      businessUnitId: trustpilot.businessUnitId,
      reviewUrl,
      reviewLanguage: defaultLanguage,
    };
  });

  public readonly trustpilotSocialProofVisible = computed<boolean>(
    () => this.trustpilotProfile() !== null,
  );

  /**
   * Platform totals for marketing copy, from `COUNTRIES[].statistics` in
   * `@handyhand/i18n`. Pass them as `users` / `handyhanders` / `reviews` /
   * `tasks` translate params, formatted with the `number` pipe.
   */
  public readonly statistics = computed<Statistics>(
    () => getCountryConfig(this.countryCode()).statistics,
  );

  /**
   * Damage compensation limits in the smallest currency unit, per currency.
   * Same source as handyhand-web's claim form — never hardcode the amounts.
   */
  private readonly compensation = computed(() =>
    resolveCompensationFromCurrencyCode(this.currencyCode()),
  );

  public readonly compensationMaximum = computed<number>(
    () => this.compensation().maximumAmount,
  );

  public readonly compensationMinimum = computed<number>(
    () => this.compensation().minimumAmount,
  );

  /** 0 where the market has no deductible (NL). */
  public readonly compensationDeductible = computed<number>(
    () => this.compensation().deductibleAmount,
  );

  /**
   * Seeded from `LanguageToken` — the same cookie-aware resolution
   * (cookie → domain → country default) that drives `LOCALE_ID` — so
   * `bcp47()` and Angular's locale formatting can never disagree when a
   * language cookie differs from the domain default.
   */
  public readonly languageCode = signal<LanguageCode>(inject(LanguageToken));

  private resolveHostname(): string {
    if (isPlatformBrowser(this.platformId)) {
      return window.location?.hostname ?? '';
    }
    if (this.request?.url) {
      try {
        return new URL(this.request.url).hostname;
      } catch {
        return '';
      }
    }
    return '';
  }

  /**
   * Resolve a named app route to a URL path using the hostname-resolved
   * country. Use from TypeScript; templates should use the `| route` pipe.
   */
  public route<N extends RouteName>(
    name: N,
    params?: ParamsOf<N>,
    options?: Omit<RouteOptions, 'country'>,
  ): string {
    const resolverOptions: RouteOptions = { country: this.routingCountryCode };

    if (options?.['query'] !== undefined) {
      resolverOptions['query'] = options['query'];
    }

    if (options?.['absolute'] !== undefined) {
      resolverOptions['absolute'] = options['absolute'];
    }

    return route(name, params, resolverOptions);
  }

  /**
   * Resolve a named SEO route to a URL. SEO routes are the static landing
   * pages served by THIS project. Uses the same hostname-locked country as
   * `route()`. Templates should use the `| seoRoute` pipe.
   */
  public seoRoute<N extends SeoRouteName>(
    name: N,
    params?: SeoParamsOf<N>,
    options?: Omit<RouteOptions, 'country'>,
  ): string {
    const resolverOptions: RouteOptions = { country: this.routingCountryCode };

    if (options?.['query'] !== undefined) {
      resolverOptions['query'] = options['query'];
    }

    if (options?.['absolute'] !== undefined) {
      resolverOptions['absolute'] = options['absolute'];
    }

    return seoRoute(name, params, resolverOptions);
  }
}
